import { Link } from "react-router-dom";
import { AmbientVideo, VideoEmbed } from "@/components/media/VideoEmbed";
import { ArrowRight, Play } from "lucide-react";

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`mono text-[11px] tracking-[0.35em] text-primary ${className}`}>{children}</div>
);

const TEXTURES = {
  graffiti: { image: "/brand/art-graffiti-tags.jpg", size: "760px auto", opacity: 0.055 },
  slime: { image: "/brand/art-slime.jpg", size: "900px auto", opacity: 0.05 },
  tribal: { image: "/brand/tribal-pattern.jpg", size: "auto 520px", opacity: 0.05 },
};

/** Faint graffiti / Polynesian pattern behind a section. Text stays on top. */
export const Texture = ({ kind }) => {
  const tex = TEXTURES[kind];
  if (!tex) return null;
  return (
    <>
      <div
        aria-hidden
        data-testid={`texture-${kind}`}
        className="tex-layer"
        style={{ backgroundImage: `url(${tex.image})`, backgroundSize: tex.size, opacity: tex.opacity }}
      />
      <div aria-hidden className="tex-fade" />
    </>
  );
};

/** Thin Polynesian band used to break up the black between sections. */
export const TribalRule = ({ testId = "tribal-rule" }) => (
  <div
    data-testid={testId}
    aria-hidden
    className="h-9 w-full border-y border-border/60 md:h-11"
    style={{
      backgroundImage: "url(/brand/tribal-pattern.jpg)",
      backgroundSize: "auto 420px",
      backgroundPosition: "center",
      backgroundRepeat: "repeat-x",
      filter: "grayscale(1) invert(1)",
      opacity: 0.14,
    }}
  />
);

export const Section = ({ children, className = "", testId, id, tint = false, texture }) => (
  <section
    id={id}
    data-testid={testId}
    className={`${tint ? "border-y border-border bg-[#0d0d0d]" : ""} ${texture ? "tex" : ""}`}
  >
    <Texture kind={texture} />
    <div className={`mx-auto max-w-6xl px-5 py-16 md:px-10 md:py-28 ${className}`}>{children}</div>
  </section>
);

export const Hero = ({ hero }) => (
  <div className="relative overflow-hidden border-b border-border" data-testid="hero">
    <AmbientVideo
      url={hero.clip}
      poster={hero.poster}
      className="absolute inset-0 h-full w-full object-cover opacity-40"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/85 to-[#0A0A0A]" />
    <div className="relative mx-auto max-w-6xl px-5 py-20 md:px-10 md:py-36">
      <h1 className="neon max-w-4xl text-4xl leading-[0.95] sm:text-5xl lg:text-7xl">
        {hero.headline_top}
        <br />
        <span className="text-primary">{hero.headline_bottom}</span>
      </h1>
      <div className="mt-7 max-w-2xl space-y-4 text-base text-muted-foreground md:text-lg">
        {(hero.body || "").split("\n\n").map((p) => (
          <p key={p.slice(0, 24)}>{p}</p>
        ))}
      </div>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
        <Link
          to={hero.primary_href}
          data-testid="hero-cta-primary"
          className="display group inline-flex items-center justify-center gap-2 rounded-sm bg-primary px-7 py-4 text-xl uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
        >
          <Play size={17} fill="currentColor" />
          {hero.primary_cta}
        </Link>
        <Link
          to={hero.secondary_href}
          data-testid="hero-cta-secondary"
          className="display inline-flex items-center justify-center gap-2 rounded-sm border border-primary px-7 py-4 text-xl uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          {hero.secondary_cta}
        </Link>
      </div>
    </div>
  </div>
);

export const StoryCard = ({ story, testId }) => (
  <article
    data-testid={testId || `story-${story.slug}`}
    className="group flex flex-col border border-border bg-[#141414] transition-colors hover:border-primary/60"
  >
    <VideoEmbed
      url={story.video_url}
      poster={story.thumbnail}
      testId={`story-video-${story.slug}`}
      aspect="9/16"
      className="max-h-[460px]"
    />
    <div className="flex flex-1 flex-col p-5">
      <h3 className="display text-3xl uppercase leading-none">{story.name}</h3>
      <div className="mono mt-1 text-[10px] tracking-widest text-primary">{story.business}</div>
      {story.lemon ? (
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          <span className="mono text-[10px] tracking-widest text-white">THE LEMON: </span>
          {story.lemon}
        </p>
      ) : null}
      {story.built ? (
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          <span className="mono text-[10px] tracking-widest text-white">WHAT THEY BUILT: </span>
          {story.built}
        </p>
      ) : null}
      <Link
        to={`/stories/${story.slug}`}
        data-testid={`story-cta-${story.slug}`}
        className="display mt-auto pt-6 inline-flex items-center gap-2 text-xl uppercase text-primary hover:underline"
      >
        Watch story
        <ArrowRight size={16} />
      </Link>
    </div>
  </article>
);

export const ClipCard = ({ clip }) => (
  <article
    data-testid={`clip-${clip.slug}`}
    className="group flex flex-col border border-border bg-[#141414] transition-colors hover:border-primary/60"
  >
    <VideoEmbed
      url={clip.video_url}
      poster={clip.thumbnail}
      testId={`clip-video-${clip.slug}`}
      aspect="9/16"
      className="max-h-[420px]"
    />
    <div className="p-4">
      <div className="mono text-[9px] tracking-widest text-primary">{clip.category}</div>
      <p className="mt-2 text-sm leading-snug text-white">{clip.title}</p>
    </div>
  </article>
);

export const LessonCard = ({ lesson }) => (
  <Link
    to={`/learn/${lesson.slug}`}
    data-testid={`lesson-${lesson.slug}`}
    className="group flex flex-col border border-border bg-[#141414] p-5 transition-all hover:-translate-y-1 hover:border-primary/60"
  >
    <span className="mono text-[9px] tracking-widest text-muted-foreground">
      {String(lesson.order).padStart(2, "0")} · FREE
    </span>
    <h3 className="display mt-3 text-2xl uppercase leading-none">{lesson.title}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{lesson.blurb}</p>
    <span className="mono mt-5 inline-flex items-center gap-1 text-[10px] tracking-widest text-primary">
      READ IT <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
    </span>
  </Link>
);

export const Timeline = ({ timeline }) => (
  <>
    <Eyebrow>DOCUMENTED, NOT POLISHED</Eyebrow>
    <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{timeline.title}</h2>
    <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{timeline.intro}</p>
    <ol className="mt-12 border-l border-border pl-5 md:pl-10">
      {(timeline.milestones || []).map((m, i) => (
        <li
          key={m.id}
          data-testid={`timeline-${m.id}`}
          className="relative pb-12 last:pb-0"
        >
          <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-primary md:-left-[46px]" />
          <div className="mono text-[10px] tracking-[0.3em] text-primary">
            {String(i + 1).padStart(2, "0")} · {m.label}
          </div>
          <p className="display mt-2 max-w-2xl text-3xl uppercase leading-none md:text-4xl">
            {m.body}
          </p>
          {m.media ? (
            <div className="mt-5 max-w-md">
              <VideoEmbed
                url={m.media}
                testId={`timeline-media-${m.id}`}
                aspect={/\.(jpg|jpeg|png|webp)$/i.test(m.media) ? "4/3" : "16/9"}
              />
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  </>
);
